import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import userService from '@/modules/user/services/userService';
import { User } from '@/services/authService';
import toast from 'react-hot-toast';

interface UserConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserWithPermissions extends User {
  _id?: string | { $oid?: string };
  permissions?: string[];
  roles?: string[];
  user_name?: string;
  mail?: string;
  code?: string;
}

const extractUserId = (user: UserWithPermissions): string => {
  for (const key of ['_id', 'id'] as const) {
    const value = user[key];
    if (!value) continue;
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && '$oid' in value && value.$oid) {
      return value.$oid;
    }
  }
  return '';
};

const getUserId = (user: UserWithPermissions): string => extractUserId(user);

const normalizeUser = (user: UserWithPermissions): UserWithPermissions => {
  const id = extractUserId(user);
  return {
    ...user,
    id,
    _id: id || (typeof user._id === 'string' ? user._id : undefined),
  };
};

const UserConfigModal: React.FC<UserConfigModalProps> = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithPermissions | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>([
    'view_users',
    'create_users',
    'edit_users',
    'delete_users',
    'view_books',
    'create_books',
    'edit_books',
    'delete_books',
    'view_schools',
    'create_schools',
    'edit_schools',
    'delete_schools',
    'view_reports',
    'manage_permissions',
    'view_dashboard',
    'manage_settings'
  ]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([
    'admin',
    'teacher',
    'student',
    'school_admin',
    'super_admin'
  ]);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers({ per_page: 100 });
      const normalizedUsers = (response.data || []).map((user) =>
        normalizeUser(user as UserWithPermissions)
      );
      setUsers(normalizedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // const fetchAvailableRoles = async () => {
  //   try {
  //     const roles = await permissionService.getAvailableRoles();
  //     setAvailableRoles(roles);
  //   } catch (error) {
  //     console.error('Error fetching roles:', error);
  //     // Fallback to default roles
  //     setAvailableRoles([
  //       'admin',
  //       'teacher',
  //       'student',
  //       'school_admin',
  //       'super_admin'
  //     ]);
  //   }
  // };

  const handleUserSelect = async (user: UserWithPermissions) => {
    const normalizedUser = normalizeUser(user);
    setSelectedUser(normalizedUser);
    setPermissions(normalizedUser.permissions || []);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    
    // Set roles based on user's current role
    const userRoles = normalizedUser.roles || (normalizedUser.role ? [normalizedUser.role] : []);
    setRoles(userRoles);
  };

  const generatePassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setNewPassword(password);
    setConfirmPassword(password);
    setShowPassword(true);
  };

  const handleChangePassword = async () => {
    if (!selectedUser) return;

    const userId = getUserId(selectedUser);
    if (!userId) {
      toast.error('Không xác định được người dùng để cập nhật mật khẩu');
      return;
    }

    if (!newPassword) {
      toast.error('Vui lòng nhập mật khẩu mới');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Mật khẩu xác nhận không khớp');
      return;
    }

    try {
      setChangingPassword(true);
      await userService.updateUserPassword(userId, newPassword);
      toast.success('Cập nhật mật khẩu thành công!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật mật khẩu');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    const userId = getUserId(selectedUser);
    const userName = selectedUser.user_name || selectedUser.name;
    if (!userId) {
      toast.error('Không xác định được người dùng để đặt lại mật khẩu');
      return;
    }

    try {
      setResettingPassword(true);
      const result = await userService.resetPassword(userId);
      toast.success(
        <div className="flex flex-col space-y-2">
          <div className="font-medium">Đặt lại mật khẩu thành công!</div>
          <div className="text-sm">
            <strong>Người dùng:</strong> {userName}
          </div>
          <div className="text-sm">
            <strong>Mật khẩu mới:</strong> {result.plain_password}
          </div>
          <button
            onClick={() => navigator.clipboard.writeText(result.plain_password)}
            className="text-xs bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded transition-colors"
          >
            Copy mật khẩu
          </button>
        </div>,
        { duration: 15000 }
      );
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi đặt lại mật khẩu');
    } finally {
      setResettingPassword(false);
    }
  };

  const handlePermissionToggle = (permission: string) => {
    setPermissions(prev => 
      prev.includes(permission) 
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const handleRoleToggle = (role: string) => {
    setRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    const userId = getUserId(selectedUser);
    if (!userId) {
      toast.error('Không xác định được người dùng để cập nhật quyền');
      return;
    }

    try {
      setSaving(true);

      await userService.updateUserRoles(userId, roles);

      setUsers(prev => prev.map(user =>
        getUserId(user) === userId
          ? { ...user, roles, permissions, role: roles[0] || user.role }
          : user
      ));
      setSelectedUser(prev => prev ? { ...prev, roles, permissions, role: roles[0] || prev.role } : prev);

      toast.success('Cập nhật quyền thành công!');
    } catch (error: any) {
      console.error('Error saving permissions:', error);
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật quyền!');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Cấu hình quyền người dùng</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* User List */}
          <div className="w-1/3 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Tìm kiếm người dùng..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {users.map((user) => (
                    <button
                      key={getUserId(user)}
                      onClick={() => handleUserSelect(user)}
                      className={`w-full text-left p-3 rounded-md transition-colors ${
                        selectedUser && getUserId(selectedUser) === getUserId(user)
                          ? 'bg-blue-50 border border-blue-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-600 text-sm font-medium">
                          {user.user_name?.charAt(0).toUpperCase() || user.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user.user_name || user.mail}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Permission Configuration */}
          <div className="flex-1 flex flex-col">
            {selectedUser ? (
              <>
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Cấu hình quyền cho: {selectedUser.name || selectedUser.user_name}
                  </h3>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  {/* Roles */}
                  <div className="mb-8">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Vai trò</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {availableRoles.map((role) => (
                        <label
                          key={role}
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={roles.includes(role)}
                            onChange={() => handleRoleToggle(role)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700 capitalize">{role.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Password */}
                  <div className="mb-8">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Mật khẩu</h4>
                    <div className="space-y-4 max-w-xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mật khẩu mới
                        </label>
                        <div className="flex gap-2">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Nhập mật khẩu mới"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            {showPassword ? 'Ẩn' : 'Hiện'}
                          </button>
                          <button
                            type="button"
                            onClick={generatePassword}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Tạo ngẫu nhiên
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Xác nhận mật khẩu
                        </label>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Nhập lại mật khẩu mới"
                        />
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={handleChangePassword}
                          disabled={changingPassword || !newPassword}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {changingPassword ? 'Đang cập nhật...' : 'Cập nhật mật khẩu'}
                        </button>
                        <button
                          type="button"
                          onClick={handleResetPassword}
                          disabled={resettingPassword}
                          className="px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50"
                        >
                          {resettingPassword ? 'Đang đặt lại...' : 'Tạo mật khẩu ngẫu nhiên (6 ký tự)'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Đặt lại mật khẩu sẽ tạo mật khẩu ngẫu nhiên 6 ký tự (chữ và số) và hiển thị để copy.
                      </p>
                    </div>
                  </div>

                  {/* Permissions */}
                  {/* <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">Quyền hạn</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {availablePermissions.map((permission) => (
                        <label
                          key={permission}
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={permissions.includes(permission)}
                            onChange={() => handlePermissionToggle(permission)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700 capitalize">{permission.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div> */}
                </div>

                <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleSavePermissions}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-gray-500">Chọn một người dùng để cấu hình quyền</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserConfigModal;
